import { useTheme } from '../../hooks/useTheme';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { FormProvider, Controller } from 'react-hook-form';
import { Tile, Button, Select, MenuItem, Input, TextArea } from '@dsi/react-eds';
import { useEnmForm } from './useEnmForm';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { EnmFormData, EnmData, EnvironmentCategory } from './EnmInterfaces';
import { useLoader } from '../../hooks/useLoader';
import api from '../../api/axiosInstance';
import { AxiosError } from 'axios';
import styles from './AddEditEnm.module.css';
import { EiapCluster } from './EiapClusterInterfaces';
import { LOADER_DEBOUNCE_MS } from '../../const/global-constants';

const AddEditEnm = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { enmId } = useParams<{ enmId?: string }>();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();
  const [showPasswords, setShowPasswords] = useState({
    password: false,
  });
  const [existingEnm, setExistingEnm] = useState<EnmData | null>(null);
  const [clusters, setClusters] = useState<EiapCluster[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const form = useEnmForm();

  // Fetch existing ENM data if editing
  useEffect(() => {
    fetchEiapClusters();
    if (enmId) {
      fetchEnmData(enmId);
    } else {
      setIsLoading(false);
    }
  }, [enmId]);

  const fetchEiapClusters = async () => {
    try {
      showLoader();
      const result = await api.get('/api/eiap-cluster');
      setClusters(result.data);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching EIAP clusters.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  }

  const fetchEnmData = async (id: string) => {
    try {
      showLoader();
      const response = await api.get(`/api/enm/${id}`);     
      const enm = response.data;
      setExistingEnm(response.data);

      // Set form values with fetched data
      form.reset({
        name: enm.name,
        category: enm.category,
        description: enm.description,
        url: enm.url,
        username: enm.username,
        password: enm.password,
        sftpIp: enm.sftpIp,
        sftpPath: enm.sftpPath,
        tlsCertificate: enm.tlsCertificate,
        linkedEiapClusters: enm.linkedEiapClusters || [],
      });

      setIsLoading(false);
      hideLoader();
    } catch (error) {
      hideLoader();
      setIsLoading(false);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred while fetching ENM data.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
      // Navigate back if ENM not found
      navigate('/integrations/enms');
    }
  };

  const categoryOptions: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];

  const convertFormDataToEnmData = (data: EnmFormData): EnmData => {
    return {
      id: existingEnm?.id || undefined,
      name: data.name,
      category: data.category,
      description: data.description,
      url: data.url,
      username: data.username,
      password: data.password,
      sftpIp: data.sftpIp,
      sftpPath: data.sftpPath,
      tlsCertificate: data.tlsCertificate,
      linkedEiapClusters: data.linkedEiapClusters,
      status: 'connected',
    };
  };

  const saveData = async (data: EnmData) => {
    showLoader();
    try {
      // Use the correct backend endpoint
      if (enmId) {
        // Update existing ENM
        await api.put(`/api/enm/${enmId}`, data, {
          headers: { 'Content-Type': 'application/json' },
        });
      } else {
        // Create new ENM
        await api.post('/api/enm', data, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      addNotifications([
        {
          title: 'Success',
          icon: 'icon-success',
          description: `ENM ${enmId ? 'updated' : 'created'} successfully!`,
          timestamp: new Date(),
          color: 'green',
        },
      ]);

      hideLoader();
      navigate('/integrations/enms');
    } catch (error) {
      console.error('Error while saving ENM: ', error);
      hideLoader();
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: 'An unexpected error occurred while saving ENM.',
          timestamp: new Date(),
          color: 'red',
        },
      ]);
    }
  };

  const handleSave = () => {
    form.handleSubmit(
      (data: any) => {
        console.log('ENM Form data on save:', data);
        console.log('ENM Category value:', data.category);
        const enmData = convertFormDataToEnmData(data as unknown as EnmFormData);
        console.log('ENM Converted data:', enmData);
        saveData(enmData);
      },
      (errors: any) => {
        console.log('FORM ERRORS: ', errors);
        addNotifications([
          {
            title: 'Validation Error',
            icon: 'icon-triangle-warning',
            description: 'Please fix the errors in the form before saving.',
            timestamp: new Date(),
            color: 'red',
          },
        ]);
      }
    )();
  };

  const handleCancel = () => {
    navigate('/integrations/enms');
  };

  const renderError = (error: any) => {
    if (!error) return null;
    return (
      <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
        <span aria-label="error" role="img">
          ⚠{' '}
        </span>
        {error.message as string}
      </span>
    );
  };

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
        <div style={{ padding: '24px', textAlign: 'center' }}>
          <p>Loading ENM data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${theme ? 'light-theme' : 'dark-theme'}`}>
      {/* Page Header */}
      <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
        <h2 style={{ marginBottom: '8px' }}>{enmId ? 'Edit ENM' : 'Create New ENM'}</h2>
        <p style={{ color: '#6a6a6a' }}>
          {enmId
            ? 'Update ENM configuration with connection details and SFTP settings'
            : 'Configure a new ENM instance with connection details and SFTP settings'}
        </p>
      </div>

      <FormProvider {...form}>
        <form id="enm-form">
          <div className="row">
            {/* ENM Information Tile */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} id="enm-information" title="ENM Information">
                <div className="row form-body">
                  <div className="lg-6 md-12 sm-12">
                    <div className="row">
                      <div className="column lg-6 md-12 sm-12">
                        <div className={styles.formElement}>
                          <label htmlFor="enm-name" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            ENM Name <span style={{ color: '#008CFF' }}>*</span>
                          </label>
                          <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                              <ReactEDSWrapper
                                component={Input}
                                id="enm-name"
                                type="text"
                                placeholder="e.g., ENM Production 01"
                                {...field}
                              />
                            )}
                          />
                          {renderError((form.formState.errors as any).name)}
                        </div>
                      </div>
                      <div className="column lg-6 md-12 sm-12">
                        <div className={styles.formElement}>
                          <label htmlFor="enm-category" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Environment Category <span style={{ color: '#008CFF' }}>*</span>
                          </label>
                          <Controller
                            name="category"
                            control={form.control}
                            render={({ field }) => (
                              <div>
                                <ReactEDSWrapper
                                  component={Select}
                                  className={styles.allWidthSelect}
                                  id="enm-category"
                                  value={field.value}
                                  onChange={(value: any) => {
                                    const stringValue = Array.isArray(value) ? value[0] : value;
                                    field.onChange(stringValue);
                                  }}
                                >
                                  {categoryOptions.map((cat, index) => (
                                    <ReactEDSWrapper component={MenuItem} key={index} value={cat}>
                                      {cat}
                                    </ReactEDSWrapper>
                                  ))}
                                </ReactEDSWrapper>
                              </div>
                            )}
                          />
                          {renderError((form.formState.errors as any).category)}
                        </div>
                      </div>
                      <div className="column lg-12">
                        <div className={styles.formElement}>
                          <label htmlFor="enm-description" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                            Description <span style={{ color: '#008CFF' }}>*</span>
                          </label>
                          <Controller
                            name="description"
                            control={form.control}
                            render={({ field }) => (
                              <ReactEDSWrapper
                                component={TextArea}
                                id="enm-description"
                                rows={3}
                                placeholder="Describe the purpose of this ENM instance..."
                                {...field}
                              />
                            )}
                          />
                          {renderError((form.formState.errors as any).description)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            {/* Connection Details Tile */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} id="connection-details" title="Connection Details">
                <div className="row form-body">
                  <div className="column sm-12 md-12 lg-6">
                    <div className={styles.formElement}>
                      <label htmlFor="enm-url" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        ENM URL <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="url"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={Input}
                            id="enm-url"
                            type="url"
                            placeholder="https://enm.example.com"
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).url)}
                    </div>

                    <div className={styles.formElement}>
                      <label htmlFor="enm-username" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Username <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="username"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={Input}
                            id="enm-username"
                            type="text"
                            placeholder="Username"
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).username)}
                    </div>

                    <div className={styles.formElement}>
                      <label htmlFor="enm-password" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        Password <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="password"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={Input}
                            id="enm-password"
                            type={showPasswords.password ? 'text' : 'password'}
                            placeholder="Password"
                            icon={`icon ${showPasswords.password ? 'icon-invisible' : 'icon-eye'}`}
                            onIconClick={() =>
                              setShowPasswords({ ...showPasswords, password: !showPasswords.password })
                            }
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).password)}
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            {/* SFTP Configuration Tile */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} id="sftp-configuration" title="SFTP Configuration">
                <div className="row form-body">
                  <div className="column sm-12 md-12 lg-6">
                    <div className={styles.formElement}>
                      <label htmlFor="enm-sftp-ip" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        SFTP IP Address <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="sftpIp"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={Input}
                            id="enm-sftp-ip"
                            type="text"
                            placeholder="192.168.1.100"
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).sftpIp)}
                    </div>

                    <div className={styles.formElement}>
                      <label htmlFor="enm-sftp-path" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        SFTP Path <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="sftpPath"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={Input}
                            id="enm-sftp-path"
                            type="text"
                            placeholder="/opt/ericsson/enm/data"
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).sftpPath)}
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            {/* TLS Certificate Tile */}
            <div className="column sm-12">
              <ReactEDSWrapper component={Tile} id="tls-certificate" title="TLS Certificate">
                <div className="row form-body">
                  <div className="column sm-12 md-12 lg-6">
                    <div className={styles.formElement}>
                      <label htmlFor="enm-tls-cert" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                        TLS Certificate <span style={{ color: '#008CFF' }}>*</span>
                      </label>
                      <Controller
                        name="tlsCertificate"
                        control={form.control}
                        render={({ field }) => (
                          <ReactEDSWrapper
                            component={TextArea}
                            id="enm-tls-cert"
                            rows={6}
                            placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                            {...field}
                          />
                        )}
                      />
                      {renderError((form.formState.errors as any).tlsCertificate)}
                    </div>
                  </div>
                </div>
              </ReactEDSWrapper>
            </div>

            <div className='column lg-12'>
                <ReactEDSWrapper component={Tile} title='Link this ENM to an EIAP Cluster' id='link-eiap-clister'>
                  <p style={{ color: '#6a6a6a', marginBottom: '16px' }}>
                    You can select an existing EIAP Cluster to link this ENM to it. This step is optional. <br/>
                    You can assign it later or link the ENM during cluster creation.
                  </p>

                  <label htmlFor="enm-eiap-cluster" style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                    EIAP Clusters
                  </label>
                  <Controller
                    name="linkedEiapClusters"
                    control={form.control}
                    render={({ field }) => (
                      <div>
                        <ReactEDSWrapper
                          component={Select}
                          id='enm-eiap-cluster'
                          type="input-single"
                          label="Select EIAP Cluster"
                          value={field.value}
                          onChange={(value: any) => {
                            field.onChange(value);
                          }}
                        >
                          {clusters.map((cluster) => (
                            <ReactEDSWrapper key={cluster.id} component={MenuItem} value={cluster.id}>
                              {`${cluster.name} (${cluster.category})`}
                            </ReactEDSWrapper>
                          ))}
                        </ReactEDSWrapper>
                      </div>
                    )}
                  />

                </ReactEDSWrapper>
            </div>

            {/* Action Buttons */}
            <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark} column sm-12`}>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <ReactEDSWrapper component={Button} variant="secondary" onClick={handleCancel}>
                  Cancel
                </ReactEDSWrapper>
                <ReactEDSWrapper component={Button} primary onClick={handleSave}>
                  {enmId ? 'Update' : 'Create'}
                </ReactEDSWrapper>
              </div>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default AddEditEnm;
